import type { CoreApiSchemas } from '@hive/contracts';
import { session } from '../auth/session';

export type ScreenEvent = CoreApiSchemas['ScreenEvent'];

const baseUrl = import.meta.env.VITE_API_BASE_URL as string;

/**
 * Subscribes to GET /v1/stream (server-sent events). Uses fetch rather than
 * EventSource so the access token travels in the Authorization header; works the same
 * in a browser and in a Capacitor WebView. Reconnects with backoff until stopped.
 */
export function subscribeScreenEvents(
  onEvent: (event: ScreenEvent) => void,
  onConnection: (connected: boolean) => void,
): () => void {
  let stopped = false;
  let controller: AbortController | null = null;
  let backoff = 1000;

  async function run() {
    while (!stopped) {
      const token = session.token();
      if (!token) return;
      controller = new AbortController();
      try {
        const res = await fetch(`${baseUrl}/v1/stream`, {
          headers: { Authorization: `Bearer ${token}`, Accept: 'text/event-stream' },
          signal: controller.signal,
        });
        if (res.status === 401) {
          session.clear();
          return;
        }
        if (!res.ok || !res.body) throw new Error(`stream ${res.status}`);
        onConnection(true);
        backoff = 1000;
        await read(res.body, onEvent);
      } catch {
        /* network error or abort; fall through to reconnect */
      }
      onConnection(false);
      if (stopped) return;
      await new Promise((r) => setTimeout(r, backoff));
      backoff = Math.min(backoff * 2, 15000);
    }
  }

  void run();
  return () => {
    stopped = true;
    controller?.abort();
  };
}

async function read(body: ReadableStream<Uint8Array>, onEvent: (event: ScreenEvent) => void) {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) return;
    buffer += decoder.decode(value, { stream: true });
    let sep: number;
    while ((sep = buffer.indexOf('\n\n')) >= 0) {
      const lines = buffer.slice(0, sep).split('\n');
      buffer = buffer.slice(sep + 2);
      if (!lines.includes('event: screen')) continue;
      const data = lines.find((l) => l.startsWith('data: '));
      if (data) onEvent(JSON.parse(data.slice(6)) as ScreenEvent);
    }
  }
}
